/**
 * Where the incident reporter starts, and what its review step shows.
 *
 * Two front doors render the same reporter. On `/tickets/new` nobody has asked
 * anything yet, so it opens on "describe" and Continue runs triage for 0-3
 * follow-up questions. From the assistant's card the chat has already done the
 * asking: the card opens straight on the review step with the assistant's
 * questions and the answers it heard, the "Anything else?" box and its
 * attachments, and a Submit button. Triage never adds new questions there —
 * a second round of questions after the conversation already asked is exactly
 * what the chat-first flow removes — and it never auto-submits either.
 *
 * Pure and import-free so `node --test` can load it straight from source.
 */

/** One question as the reporter renders it. */
export interface ReviewQuestion {
  id: string;
  text: string;
}

/** A question the assistant asked, with what it heard back. */
export interface ChatFollowup {
  question: string;
  answer?: string | null;
}

export type ReporterStep = "describe" | "questions";

/** Where the reporter opens, with the questions and answers to seed it with. */
export interface ReporterStart {
  step: ReporterStep;
  /** The questions on screen from the first render; empty on "describe". */
  questions: ReviewQuestion[];
  /** Answers keyed by question id, seeded from the chat. */
  answers: Record<string, string>;
}

/** The assistant's follow-ups as reporter questions, blanks dropped. */
export function chatFollowupQuestions(followups: readonly ChatFollowup[] = []): {
  questions: ReviewQuestion[];
  answers: Record<string, string>;
} {
  const asked = followups.filter((item) => item.question.trim() !== "");
  return {
    questions: asked.map((item, index) => ({ id: `chat${index + 1}`, text: item.question })),
    answers: Object.fromEntries(
      asked.map((item, index) => [`chat${index + 1}`, item.answer ?? ""]),
    ),
  };
}

/**
 * The reporter's first render.
 *
 * `fromChat` opens on the review step — but only when what the assistant
 * prefilled would pass validation (`prefillValid`). A description too short to
 * file falls back to "describe" with the error on show, so the first thing the
 * person sees is what to fix.
 */
export function reporterStart(
  followups: readonly ChatFollowup[] | undefined,
  { fromChat, prefillValid }: { fromChat: boolean; prefillValid: boolean },
): ReporterStart {
  const { questions, answers } = chatFollowupQuestions(followups);
  if (fromChat && prefillValid) {
    return { step: "questions", questions, answers };
  }
  return { step: "describe", questions: [], answers };
}

/**
 * Whether the review step shows the "Anything else?" box (and its uploader).
 *
 * On `/tickets/new` it closes a round of real questions only: zero questions
 * there means triage found nothing to ask and the ticket files at once. From
 * the chat it is always on the review step, questions or not — it is the one
 * place left to add detail or a screenshot before sending.
 */
export function showsAnythingElse({
  step,
  questionCount,
  fromChat,
}: {
  step: ReporterStep;
  questionCount: number;
  fromChat: boolean;
}): boolean {
  return step === "questions" && (fromChat || questionCount > 0);
}

/**
 * Whether "Skip & submit" is offered. Only for triage's own questions: the
 * chat's were already answered in the conversation, and skipping would throw
 * those answers away.
 */
export function offersSkip({
  questionCount,
  fromChat,
}: {
  questionCount: number;
  fromChat: boolean;
}): boolean {
  return !fromChat && questionCount > 0;
}
